/**
 * Hours logged, by month, by person, by project.
 *
 * A running clock is invisible here, and that is deliberate rather than an
 * omission: an unstopped timer lives in its own table and has no minutes yet. A
 * report that guessed at them would let somebody bill for a clock they forgot to
 * stop.
 */
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { fmtNum } from "../../ui";
import { toHours } from "../../../lib/reports";
import { Breakdown, MonthChart, Stat, StatStrip } from "./bits";
import type { TimesheetsReport } from "../../../lib/reports";

/** Minutes as hours, the way a timesheet line reads. The conversion happens here
 *  and nowhere earlier, so the per-person rows still add up to the total. */
function hours(minutes: number): string {
  return `${fmtNum(toHours(minutes))} h`;
}

export function TimesheetsReportTab({ data }: { data: TimesheetsReport }) {
  const t = useT();
  const { locale } = useI18n();
  const { totals } = data;
  const unbilled = Math.max(0, totals.billableMinutes - totals.billedMinutes);

  return (
    <>
      <StatStrip columns={4}>
        <Stat label={t("reports.time.total")} value={hours(totals.minutes)} sub={fmtNum(totals.entries) + " " + t("reports.time.entriesUnit")} />
        <Stat label={t("reports.time.billable")} value={hours(totals.billableMinutes)} />
        <Stat label={t("reports.time.billed")} value={hours(totals.billedMinutes)} tone="ok" />
        <Stat label={t("reports.time.unbilled")} value={hours(unbilled)} tone={unbilled ? "bad" : undefined} />
      </StatStrip>

      <div className="crm-rep-grid">
        <MonthChart
          title={t("reports.time.chartTitle")}
          sub={t("reports.time.chartSub")}
          months={data.meta.months}
          locale={locale}
          lines={[
            { label: t("reports.time.total"), values: data.months.map((m) => toHours(m.minutes)), color: "var(--color-primary)" },
            { label: t("reports.time.billable"), values: data.months.map((m) => toHours(m.billableMinutes)), color: "var(--color-success)" },
          ]}
        />
      </div>

      <div className="crm-rep-grid is-split">
        <Breakdown
          title={t("reports.time.byUser")}
          rows={data.byUser.map((r) => ({
            key: r.userId,
            label: r.name || t("reports.unknownUser"),
            weight: r.minutes,
            value: hours(r.minutes),
            sub: t("reports.time.billableSub", { v: hours(r.billableMinutes) }),
          }))}
          empty={t("reports.empty")}
        />
        <Breakdown
          title={t("reports.time.byProject")}
          sub={t("reports.time.byProjectSub")}
          rows={data.byProject.map((r) => ({
            key: r.projectId,
            label: r.name,
            weight: r.minutes,
            value: hours(r.minutes),
            sub: t("reports.time.billableSub", { v: hours(r.billableMinutes) }),
          }))}
          empty={t("reports.empty")}
          color="var(--color-primary-2)"
        />
      </div>

      <p className="crm-rep-note">{t("reports.time.note")}</p>
    </>
  );
}
